import { useState, useEffect, useRef } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Draggable from 'react-draggable';
import { API, Auth } from 'aws-amplify';

// Context and utilities
import { useAppContext } from '../../Lib/contextLib';
import { getVideo, GetUserVideo } from '../../s3Func/s3Download';

// Components
import PopUp from '../../Popup/logoutPopup';
import { CloudfrontVideoFormat } from '../../videos/CloudfrontVideoFormat';
import Learn from '../../Learn/Learn';
import Practice from './Practice';
import SingingTime from '../../SingingTime/SingingTime';
import Celebrate from '../../Celebrate/Celebrate';
import Surprise from '../../SurpriseVideo/surprise';
import FollowTheBeatModule6 from '../../FollowTheBeat/FollowTheBeatModule6.js';

// Assets
import presentSound from '../../Unit1/Module1/assets/ta_da_gift.mp3';
import loadingGif from '../../loading-gif-png-5.gif';

// Styles
import './Module11World.css';
import '../../Animations.css';

// ============================================================================
// Constants
// ============================================================================

const AFTER_CELEBRATE_VIDEO_DAY9 = '8338f6e6d4e5093c5c4cc6bc19ab/480.m3u8';
const MODULE_NAME = 'MODULE11';
const NAVIGATION_PATH = '/feelings/navigation';
const SPECIAL_DAYS = { DAY_4: 4, DAY_8: 8 };
const COIN_PRIZE_THRESHOLD = 100;

// Step completion states
const STEPS = {
    NOT_STARTED: 'notstarted',
    LEARN: 'learn',
    PRACTICE: 'practice',
    FOLLOW_THE_BEAT: 'followthebeat',
    CELEBRATE: 'celebrate',
    SINGING_TIME: 'singingtime',
    SURPRISE: 'surprise',
};

// Grade types that bypass certain restrictions
const UNRESTRICTED_GRADES = ['Teacher Mode', 'Free Play'];

// ============================================================================
// Utility Functions
// ============================================================================

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isIOSDevice = () => {
    const iosDevices = [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod',
    ];
    return (
        iosDevices.includes(navigator.platform) ||
        (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    );
};

const isUnrestrictedGrade = (grade) => UNRESTRICTED_GRADES.includes(grade);

// ============================================================================
// Custom Hooks
// ============================================================================

const useWindowDimensions = () => {
    const [dimensions, setDimensions] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });

    useEffect(() => {
        const handleResize = () => {
            setDimensions({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return dimensions;
};

// ============================================================================
// Main Component
// ============================================================================

const Module11World = () => {
    const { width: clientWidth, height: clientHeight } = useWindowDimensions();
    const { userHasAuthenticated } = useAppContext();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Redux state
    const { module11 } = useSelector((state) => state.module11, shallowEqual);
    const { login } = useSelector((state) => state.loginReducer, shallowEqual);

    // Refs for tracking initial renders
    const firstUpdate = useRef(true);
    const coinUpdate = useRef(true);

    // ------------------------------------------------------------------------
    // Image URL Generator
    // ------------------------------------------------------------------------
    const generateImageUrl = (baseUrl) => login.cloudFront + baseUrl + login.cookie;

    // ------------------------------------------------------------------------
    // Image Assets
    // ------------------------------------------------------------------------
    const images = {
        // Background and UI
        background: generateImageUrl('images/Module11Images/Unit3_Mod1_Background-1.png'),
        closedChest: generateImageUrl('images/Module1Images/Chest_Closed.svg'),
        openChest: generateImageUrl('images/Module1Images/Chest_Open.svg'),
        animal: generateImageUrl('images/Module1Images/Crab.svg'),
        crab: generateImageUrl('images/Module1Images/Crab2.gif'),
        coinPick: generateImageUrl('images/Images/AwesomeCoin.png'),
        coinBar: generateImageUrl('images/Images/Coin+Counter.png'),
        journeyMap: generateImageUrl('images/Module1Images/bluejourneymap.svg'),
        greenCheck: generateImageUrl('images/Module1Images/GreenCheck-Button.png'),
        present: generateImageUrl('images/Images/Gift.png'),
        keyReward: generateImageUrl('images/Module1Images/Key_Full.png'),

        // Buttons
        logoutBtn: generateImageUrl('images/Module1Images/Logout.png'),
        reviewBtn: generateImageUrl('images/Module1Images/Button_Review.png'),
        rewardBtn: generateImageUrl('images/Module1Images/Button_Reward_3x.png'),
        celebrateBtn: generateImageUrl('images/Module1Images/Button_Celebrate.png'),
        learnBtn: generateImageUrl('images/Module1Images/Button_Learn.png'),
        practiceBtn: generateImageUrl('images/Module1Images/Button_Practice.png'),
        ftbBtn: generateImageUrl('images/Images/Button_FTB.svg'),
        singingTimeBtn: generateImageUrl('images/Module1Images/Button_SingingTime.png'),

        // Progress bars
        progressBars: [
            generateImageUrl('images/Module1Images/ProgressBar_0.svg'),
            generateImageUrl('images/Module1Images/ProgressBar_1-4.svg'),
            generateImageUrl('images/Module1Images/ProgressBar_2-4.svg'),
            generateImageUrl('images/Module1Images/ProgressBar_3-4.svg'),
            generateImageUrl('images/Module1Images/ProgressBar_Completed.svg'),
        ],

        // Rewards
        rewards: {
            SurfBoardBlue: generateImageUrl('images/Module1Images/Reward_SurfBoardBlue.svg'),
            SurfBoardRed: generateImageUrl('images/Module1Images/Reward_SurfBoardRed.svg'),
            CoralRed: generateImageUrl('images/Module6Images/DailyReward_CoralRed.png'),
            CoralBlue: generateImageUrl('images/Module6Images/DailyReward_CoralBlue.png'),
            FloatationBall: generateImageUrl('images/Module1Images/Reward_BeachBall.svg'),
            FloatationInnerTube: generateImageUrl('images/Module1Images/Reward_Floaty.svg'),
            SeashellsRed: generateImageUrl('images/Module1Images/Reward_StarFish.svg'),
            SeashellsGreen: generateImageUrl('images/Module1Images/Reward_Shell.svg'),
            MermaidBlue: generateImageUrl('images/Module6Images/DailyReward_Mermaid1.png'),
            MermaidRed: generateImageUrl('images/Module6Images/DailyReward_Mermaid2.png'),
            ClamBlue: generateImageUrl('images/Module6Images/DailyReward_BluePearl.png'),
            ClamPink: generateImageUrl('images/Module6Images/DailyReward_PinkPearl.png'),
            SeaweedGreen: generateImageUrl('images/Module6Images/DailyReward_SeaweedGreen@3x.png'),
            SeaweedBrown: generateImageUrl('images/Module6Images/DailyReward_SeaweedBrown@3x.png'),
            CrabwearGlasses: generateImageUrl('images/Module1Images/Reward_Glasses.svg'),
            CrabWearMustash: generateImageUrl('images/Module1Images/Reward_Mustache.svg'),
            SandCastle: generateImageUrl('images/Module6Images/BigReward_InsideCir_Castle.svg'),
            Treasure: generateImageUrl('images/Module6Images/BigReward_Treasure.svg'),
        },
    };

    // Progress bar arrays for different day types
    const progressBar5Keys = [
        images.progressBars[0],
        images.progressBars[1],
        images.progressBars[2],
        images.progressBars[2],
        images.progressBars[3],
        images.progressBars[4],
    ];
    const progressBar4Keys = images.progressBars;

    // Reward options by day
    const rewardOptionsByDay = {
        1: [images.rewards.SurfBoardRed, images.rewards.SurfBoardBlue],
        2: [images.rewards.CoralBlue, images.rewards.CoralRed],
        3: [images.rewards.FloatationInnerTube, images.rewards.FloatationBall],
        4: [images.rewards.SeashellsRed, images.rewards.SeashellsGreen],
        5: [images.rewards.MermaidRed, images.rewards.MermaidBlue],
        6: [images.rewards.ClamBlue, images.rewards.ClamPink],
        7: [images.rewards.SeaweedBrown, images.rewards.SeaweedGreen],
        8: [images.rewards.CrabwearGlasses, images.rewards.CrabWearMustash],
    };

    // Influencers array
    const influencers = [
        login.influencerid,
        login.influencerid1,
        login.influencerid2,
        login.influencerid3,
        login.influencerid4,
    ];

    // Audio for touch interactions
    const touchSound = new Audio(generateImageUrl('media/Module1Videos/touch_button.mp3'));

    // ------------------------------------------------------------------------
    // UI State
    // ------------------------------------------------------------------------
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    // Icon visibility state
    const [reviewIconVisible, setReviewIconVisible] = useState(false);
    const [keyIcon, setKeyIcon] = useState(false);
    const [coinIcon, setCoinIcon] = useState(false);
    const [presentIcon, setPresentIcon] = useState(false);

    // Reward selection state
    const [rewardOption1, setRewardOption1] = useState(null);
    const [rewardOption2, setRewardOption2] = useState(null);
    const [displayReward, setDisplayReward] = useState(false);
    const [displayGreenCheck, setDisplayGreenCheck] = useState(false);
    const [dropZone, setDropZone] = useState(false);
    const [displayGreenButton, setDisplayGreenButton] = useState(false);

    // Screen/component visibility state
    const [displayLearn, setDisplayLearn] = useState(false);
    const [displayPractice, setDisplayPractice] = useState(false);
    const [displayDay4Practice, setDisplayDay4Practice] = useState(false);
    const [displaySingingTime, setDisplaySingingTime] = useState(false);
    const [displayCelebrate, setDisplayCelebrate] = useState(false);
    const [displayBody, setDisplayBody] = useState(true);
    const [displayIntroVideo, setDisplayIntroVideo] = useState(false);
    const [displaySurprise, setDisplaySurprise] = useState(false);
    const [displayVoice, setDisplayVoice] = useState(false);
    const [displayFTB, setDisplayFTB] = useState(false);

    // Component content state
    const [learnComponent, setLearnComponent] = useState('');
    const [singTimeComponent, setSingTimeComponent] = useState('');
    const [day4PracticeComponent, setDay4PracticeComponent] = useState('');
    const [celebrateComponent, setCelebrateComponent] = useState('');
    const [introVideo, setIntroVideo] = useState('');
    const [surpriseVideo, setSurpriseVideo] = useState(false);

    // Chest state
    const [chestImage, setChestImage] = useState(images.closedChest);
    const [chestAnimationState, setChestAnimationState] = useState('');
    const [playChestOpen, setPlayChestOpen] = useState(false);
    const [playChestShake, setPlayChestShake] = useState(false);

    // Crab/animal state
    const [showCrab, setShowCrab] = useState(false);
    const [crabState, setCrabState] = useState('');
    const [animalVoice, setAnimalVoice] = useState('');

    // Prize state
    const [prize, setPrize] = useState(false);
    const [displayPrize, setDisplayPrize] = useState(false);

    // Animation state
    const [keyAnimationState, setKeyAnimationState] = useState('');
    const [coinAnimationState, setCoinAnimationState] = useState('bounceIcons');

    // Audio state
    const [touchButton, setTouchButton] = useState(false);
    const [checkSound, setCheckSound] = useState(false);
    const [buttonSound, setButtonSound] = useState(false);
    const [rewardsFound, setRewardsFound] = useState(false);
    const [coinAudio, setCoinAudio] = useState(false);
    const [playYouAreAwesome, setPlayYouAreAwesome] = useState(false);
    const [audioReady, setAudioReady] = useState(false);

    // Tracking state
    const [checkStep, setCheckStep] = useState('');
    const [showReward, setShowReward] = useState(false);

    // Follow The Beat (FTB) assets
    const [ftbMusic, setFTBMusic] = useState('');
    const [ftbInstruction, setFTBInstruction] = useState('');
    const [ftbInstructionGIF, setFTBInstructionGIF] = useState('');
    const [ftbCount, setFTBCount] = useState('');
    const [ftbTutorial1, setFTBTutorial1] = useState('');
    const [ftbTutorial2, setFTBTutorial2] = useState('');
    const [ftbWalkthrough, setFTBWalkthrough] = useState('');

    // GIF and audio assets
    const [dayFinished, setDayFinished] = useState('');
    const [ftbIntro, setFtbIntro] = useState('');
    const [practiceIntro, setPracticeIntro] = useState('');
    const [learnIntro, setLearnIntro] = useState('');
    const [awesomeJob, setAwesomeJob] = useState('');
    const [celebrateIntro, setCelebrateIntro] = useState('');
    const [closingGIF, setClosingGIF] = useState('');
    const [practiceMessage, setPracticeMessage] = useState('');
    const [practiceConversation, setPracticeConversation] = useState('');
    

    // ------------------------------------------------------------------------
    // Asset Loading
    // ------------------------------------------------------------------------

    const loadGifAssets = () => {
        // Common GIF assets
        setAwesomeJob(getVideo('appfiles/greet/greet_awesome_job.gif', login));
        setFTBCount(getVideo('appfiles/greet/countingCrab.gif', login));
        setPracticeIntro(getVideo('appfiles/greet/greet_practice_intro.gif', login));
        setLearnIntro(getVideo('appfiles/greet/greet_learn_intro.gif', login));
        setFtbIntro(getVideo('appfiles/greet/ftb_intro.gif', login));
        setCelebrateIntro(getVideo('appfiles/greet/greet_celebrate_intro.gif', login));
        setFTBInstructionGIF(getVideo('appfiles/greet/ftb_crab_instructions.gif', login));
        setClosingGIF(getVideo('appfiles/feelings/feelings_closing.gif', login));
        setPracticeMessage(getVideo('appfiles/greet/greet_special_message.gif', login));
        setPracticeConversation(getVideo('appfiles/greet/practice_watch_conversation.gif', login));

        // Audio assets
        setFTBInstruction(getVideo('appfiles/feelings/feelings_ftb_instructions.mp3', login));
        setFTBMusic(getVideo('appfiles/feelings/ftb greet with clapping.m4a', login));
        setDayFinished(getVideo('appfiles/feelings/feelings_day_finished.mp3', login));

        // Tutorial assets (only for first-time users)
        if (!module11.unlockedDays.day2) {
            setFTBTutorial1(getVideo('appfiles/feelings/ftb_walkthrough_touch.mp3', login));
            setFTBTutorial2(getVideo('appfiles/feelings/ftb_walkthrough_clap.mp3', login));
            setFTBWalkthrough(getVideo('appfiles/greet/ftb_walkthrough_audio.mp3', login));
        }
    };

    useEffect(() => {
        loadGifAssets();
    }, []);

    // ------------------------------------------------------------------------
    // Game State Management
    // ------------------------------------------------------------------------

    const createGameDetails = (flag, reward, coin) => {
        const gameDetail = { ...module11.game_details };

        if (flag) {
            const flagEntry = { flag, lastUpdated: new Date().toJSON() };
            gameDetail.moduleFlags.push(flagEntry);
        }

        if (coin >= 0) {
            gameDetail.tokens_earned = coin;
        }

        if (reward) {
            const dailyRewards = Object.entries(module11.rewards)
                .filter(([, rewardData]) => rewardData.unlocked)
                .map(([key, rewardData]) => ({
                    type: key,
                    x_multiplier: rewardData.x,
                    y_multiplier: rewardData.y,
                }));
            gameDetail.dailyRewards = dailyRewards;
        }

        dispatch({ type: 'MODULE11_SET_GameDetials', payload: gameDetail });
    };

    // ------------------------------------------------------------------------
    // API Calls
    // ------------------------------------------------------------------------

    const updateProgress = async () => {
        const payload = {
            body: {
                id: login.studentid,
                module: 'Feelings',
                day_completed: module11.currentday,
                time_completed: new Date().toDateString(),
            },
        };

        try {
            await API.put('TIR', '/v3/student/progress', payload);
        } catch (error) {
            // Silent fail - progress update is non-critical
        }
    };

    const updateDB = async () => {
        const modulePayload = {
            body: {
                id: module11.moduleID,
                day_completed: module11.day_completed,
                step_completed: module11.stepCompleted,
                game_details: JSON.stringify(module11.game_details),
            },
        };

        try {
            await API.put('TIR', '/v3/student/module', modulePayload);

            if (displayGreenButton) {
                setAnimalVoice(null);
                setCrabState(closingGIF);
                setShowCrab(true);
                setPlayYouAreAwesome(true);
                setDisplayGreenButton(false);
                setDisplayReward(false);
            }
        } catch (error) {
            const criticalSteps = [STEPS.NOT_STARTED, STEPS.LEARN, STEPS.PRACTICE, STEPS.CELEBRATE, STEPS.FOLLOW_THE_BEAT];
            if (criticalSteps.includes(module11.stepCompleted)) {
                window.alert('Connection is lost. Please login again.');
                dispatch({ type: 'CLEAR' });
                Auth.signOut();
                navigate('/');
            }
        }

        // Update current module for student
        const studentPayload = {
            body: {
                id: login.studentid,
                current_module: 'Feelings',
            },
        };

        try {
            await API.put('TIR', '/v3/student', studentPayload);
        } catch (error) {
            // Silent fail - student update is non-critical
        }
    };

    // ------------------------------------------------------------------------
    // Effects for State Synchronization
    // ------------------------------------------------------------------------

    // Sync step completion to database
    useEffect(() => {
        if (firstUpdate.current) {
            firstUpdate.current = false;
            return;
        }

        if (!isUnrestrictedGrade(login.grade)) {
            updateDB();
        }
    }, [module11.stepCompleted]);

    // Handle section completion
    useEffect(() => {
        const shouldUpdateProgress =
            (module11.reviewIcon || login.grade === 'Teacher Mode') &&
            module11.sectionCompleted === 4;

        if (shouldUpdateProgress) {
            updateProgress();
            module11.sectionCompleted = 0;
        }
    }, [module11.sectionCompleted]);

    // Handle coin updates and prize eligibility
    useEffect(() => {
        if (module11.coins >= COIN_PRIZE_THRESHOLD) {
            setPrize(true);
        }

        if (coinUpdate.current) {
            coinUpdate.current = false;
            return;
        }

        createGameDetails(false, false, module11.coins);
        updateDB();
    }, [module11.coins]);

    // ------------------------------------------------------------------------
    // UI Handlers
    // ------------------------------------------------------------------------

    const togglePopup = () => {
        setIsPopupOpen((prev) => !prev);
    };

    // Handle progress key updates based on step completion
    useEffect(() => {
        const isDayCompleted = module11.currentday <= module11.day_completed;
        const hasUnrestrictedAccess = isUnrestrictedGrade(login.grade);

        if (isDayCompleted || hasUnrestrictedAccess) {
            // Day is completed or user has unrestricted access
            if (hasUnrestrictedAccess) {
                setShowReward(true);
            }

            dispatch({ type: 'MODULE11_TOGGLE_REVIEW_ICON', payload: true });

            const isSpecialDay = module11.currentday === SPECIAL_DAYS.DAY_4 || module11.currentday === SPECIAL_DAYS.DAY_8;
            dispatch({ type: 'MODULE11_SET_KEY', payload: isSpecialDay ? 5 : 4 });
        } else {
            // Day is in progress
            if (module11.stepCompleted === checkStep) {
                setCoinIcon(true);
            }

            dispatch({ type: 'MODULE11_TOGGLE_REVIEW_ICON', payload: false });

            const isDay4Start = module11.currentday === SPECIAL_DAYS.DAY_4 && module11.day_completed === 3;

            if (isDay4Start) {
                // Day 4 has a different step sequence (includes singing time first)
                const day4KeyMap = {
                    [STEPS.NOT_STARTED]: 0,
                    [STEPS.SINGING_TIME]: 1,
                    [STEPS.LEARN]: 2,
                    [STEPS.PRACTICE]: 3,
                    [STEPS.FOLLOW_THE_BEAT]: 4,
                    [STEPS.CELEBRATE]: 5,
                };

                const keyValue = day4KeyMap[module11.stepCompleted];
                if (keyValue !== undefined) {
                    dispatch({ type: 'MODULE11_SET_KEY', payload: keyValue });
                    if (module11.stepCompleted === STEPS.CELEBRATE) {
                        setKeyIcon(true);
                    }
                }
            } else {
                // Standard day progression
                const standardKeyMap = {
                    [STEPS.NOT_STARTED]: 0,
                    [STEPS.LEARN]: 1,
                    [STEPS.PRACTICE]: 2,
                    [STEPS.FOLLOW_THE_BEAT]: 3,
                    [STEPS.CELEBRATE]: 4,
                    [STEPS.SURPRISE]: 5,
                };

                const keyValue = standardKeyMap[module11.stepCompleted];
                if (keyValue !== undefined) {
                    dispatch({ type: 'MODULE11_SET_KEY', payload: keyValue });

                    // Handle special cases for celebrate step
                    if (module11.stepCompleted === STEPS.CELEBRATE) {
                        const isDay8Surprise = module11.currentday === SPECIAL_DAYS.DAY_8 && module11.day_completed === 7;
                        if (isDay8Surprise) {
                            setPresentIcon(true);
                        } else {
                            setKeyIcon(true);
                        }
                    } else if (module11.stepCompleted === STEPS.SURPRISE) {
                        setKeyIcon(true);
                    }
                }
            }

            playButtonAudio();
        }
    }, [module11.stepCompleted]);

    // ------------------------------------------------------------------------
    // Reward Selection Handlers
    // ------------------------------------------------------------------------

    const handleRewardSelection = (selectedOption) => {
        const isOption1 = selectedOption === 1;
        const day = module11.currentday;

        // Update Redux state
        dispatch({ type: `MODULE11_SET_REWARD${day}1`, payload: isOption1 });
        dispatch({ type: `MODULE11_SET_REWARD${day}2`, payload: !isOption1 });

        // Update visual selection feedback
        document.getElementById('rewardItemLeft').style.borderColor = isOption1 ? 'yellow' : 'white';
        document.getElementById('rewardItemRight').style.borderColor = isOption1 ? 'white' : 'yellow';

        setTouchButton(true);
        setDisplayGreenCheck(true);
    };

    const handleRewardConfirmation = () => {
        document.getElementById('rewardList').style.zIndex = 1;
        setCheckSound(true);
        setDropZone(true);
        setDisplayGreenCheck(false);
        setDisplayGreenButton(true);
    };

    const handleDayCompletion = () => {
        setCheckSound(true);
        setDropZone(false);
        updateProgress();

        sleep(500).then(() => {
            createGameDetails(false, true, module11.coins);

            if (isUnrestrictedGrade(login.grade)) {
                updateDB();
            }

            const nextDay = module11.currentday + 1;
            const completedDay = module11.currentday === SPECIAL_DAYS.DAY_8 ? 9 : module11.currentday;

            dispatch({ type: `MODULE11_SET_DAY_UNLOCK${nextDay}`, payload: true });
            dispatch({ type: 'MODULE11_SET_dayCompleted', payload: completedDay });
            dispatch({ type: 'MODULE11_SET_stepCompleted', payload: STEPS.NOT_STARTED });
        });
    };

    const renderRewardSelection = () => (
        <>
            <div className="rewards" id="rewardList">
                <div className="circle" id="rewardItemLeft">
                    <img
                        src={rewardOption1}
                        alt="Reward option 1"
                        onClick={() => handleRewardSelection(1)}
                    />
                </div>
                <div className="circle" id="rewardItemRight">
                    <img
                        src={rewardOption2}
                        alt="Reward option 2"
                        onClick={() => handleRewardSelection(2)}
                    />
                </div>
            </div>

            <div className="greenCheckDiv">
                {displayGreenCheck && (
                    <img
                        className="greenCheck"
                        src={images.greenCheck}
                        alt="Confirm selection"
                        onClick={handleRewardConfirmation}
                    />
                )}
            </div>

            {dropZone && (
                <div className="area">
                    <div id="dropZone">Drag and drop your item</div>
                </div>
            )}

            <div className="greenCheckDiv">
                {displayGreenButton && (
                    <img
                        className="greenCheck"
                        src={images.greenCheck}
                        alt="Complete day"
                        onClick={handleDayCompletion}
                    />
                )}
            </div>
        </>
    );

    // ------------------------------------------------------------------------
    // Prize Selection Handlers
    // ------------------------------------------------------------------------

    const handlePrizeSelection = (selectedOption) => {
        const isOption1 = selectedOption === 1;

        dispatch({ type: 'MODULE11_SET_PRIZE1', payload: isOption1 });
        dispatch({ type: 'MODULE11_SET_PRIZE2', payload: !isOption1 });

        document.getElementById('rewardItemLeft').style.borderColor = isOption1 ? 'yellow' : 'white';
        document.getElementById('rewardItemRight').style.borderColor = isOption1 ? 'white' : 'yellow';

        setTouchButton(true);
        setDisplayGreenCheck(true);
    };

    const handlePrizeConfirmation = () => {
        setDisplayPrize(false);
        setCheckSound(true);
        setDropZone(false);

        sleep(500).then(() => {
            setDisplayGreenButton(false);
            createGameDetails(false, true);
            dispatch({ type: 'MODULE11_SET_COIN', payload: module11.coins - COIN_PRIZE_THRESHOLD });
            setChestImage(images.closedChest);
            setPlayChestOpen(false);
        });
    };

    const renderPrizeSelection = () => (
        <>
            <div className="rewards">
                <div className="circle" id="rewardItemLeft">
                    <img
                        src={images.rewards.SandCastle}
                        alt="Sand castle prize"
                        onClick={() => handlePrizeSelection(1)}
                    />
                </div>
                <div className="circle" id="rewardItemRight">
                    <img
                        src={images.rewards.Treasure}
                        alt="Treasure prize"
                        onClick={() => handlePrizeSelection(2)}
                    />
                </div>
            </div>

            <div className="greenCheckDiv">
                {displayGreenCheck && (
                    <img
                        className="greenCheck"
                        src={images.greenCheck}
                        alt="Confirm selection"
                        onClick={() => {
                            setCheckSound(true);
                            setDropZone(true);
                            setDisplayGreenCheck(false);
                            setDisplayGreenButton(true);
                        }}
                    />
                )}
            </div>

            {dropZone && (
                <div className="area">
                    <div id="dropZone">Drag and drop your item</div>
                </div>
            )}

            <div className="greenCheckDiv">
                {displayGreenButton && (
                    <img
                        className="greenCheck"
                        src={images.greenCheck}
                        alt="Confirm prize"
                        onClick={handlePrizeConfirmation}
                    />
                )}
            </div>
        </>
    );

    // Set reward options based on current day
    useEffect(() => {
        const options = rewardOptionsByDay[module11.currentday];

        if (options) {
            setRewardOption1(options[0]);
            setRewardOption2(options[1]);
        } else {
            setDisplayGreenButton(true);
        }
    }, [keyIcon]);

    // ------------------------------------------------------------------------
    // Review Mode Rendering
    // ------------------------------------------------------------------------

    const renderReviewButtons = () => {
        const isDay4 = module11.currentday === SPECIAL_DAYS.DAY_4;

        if (isDay4) {
            // Day 4 includes singing time button
            return (
                <div className="world2reviewContainer">
                    <img
                        className="reviewBtns41"
                        src={images.singingTimeBtn}
                        alt="singing button"
                        onClick={displaySinging}
                    />
                    <img
                        className="reviewBtns42"
                        src={images.learnBtn}
                        alt="learn button"
                        onClick={openLearn}
                    />
                    <img
                        className="reviewBtns43"
                        src={images.practiceBtn}
                        alt="practice button"
                        onClick={openPractice}
                    />
                    <img
                        className="reviewBtns44"
                        src={images.ftbBtn}
                        alt="ftb button"
                        onClick={openFTB}
                    />
                    <img
                        className="reviewBtns45"
                        src={images.celebrateBtn}
                        alt="celebrate button"
                        onClick={openCelebrate}
                    />
                </div>
            );
        }

        // Standard review buttons
        return (
            <div className="world2reviewContainer">
                <img
                    className="reviewBtns1"
                    src={images.learnBtn}
                    alt="learn button"
                    onClick={openLearn}
                />
                <img
                    className="reviewBtns2"
                    src={images.practiceBtn}
                    alt="practice button"
                    onClick={openPractice}
                />
                <img
                    className="reviewBtns3"
                    src={images.ftbBtn}
                    alt="ftb button"
                    onClick={openFTB}
                />
                <img
                    className="reviewBtns4"
                    src={images.celebrateBtn}
                    alt="celebrate button"
                    onClick={openCelebrate}
                />
            </div>
        );
    };

    // ------------------------------------------------------------------------
    // Video and Activity Handlers
    // ------------------------------------------------------------------------

    const handleIntroVideoEnd = () => {
        setDisplayIntroVideo(false);
        setDisplayBody(true);

        if (module11.currentday === SPECIAL_DAYS.DAY_4) {
            dispatch({ type: 'MODULE11_SET_KEY', payload: 1 });
            playButtonAudio();
        }

        if (module11.day_completed === 9) {
            navigate(NAVIGATION_PATH);
        }
    };

    const renderIntroduction = () => (
        <div>
            <CloudfrontVideoFormat
                source={introVideo}
                back={NAVIGATION_PATH}
                background={images.background}
                token={login.cookie}
                ended={handleIntroVideoEnd}
            />
        </div>
    );

    // Learn video configuration by day
    const learnVideosByDay = {
        1: { video: 'af6addb49d098d7052ee260834b1/480.m3u8', closingVideo: '979516c071834a384e6e9a022f28/480.m3u8' },
        2: { video: '51f4b4e5e4bd1dc5aac4c626a563/480.m3u8', closingVideo: 'e37c31378ff6c2d3301507dcf535/480.m3u8' },
        3: { video: '610cd2227ca26f5ad3c2cbd0d275/480.m3u8', closingVideo: 'ede27497e3530e9b95fa433577f2/480.m3u8' },
    };

    const openLearn = async () => {
        const song = getVideo('appfiles/feelings/feelings_demo.m4a', login);
        const video = getVideo('appfiles/feelings/learn_video.mp4', login);
        const dayConfig = learnVideosByDay[module11.currentday];

        if (dayConfig) {
            // Days 1-3: Use pre-configured videos
            setLearnComponent(
                <Learn
                    video={dayConfig.video}
                    songVideo={video}
                    closingVideo={dayConfig.closingVideo}
                    destiny="/feelings"
                    song={song}
                    back="/feelings"
                    background={images.background}
                    setDisplayLearn={setDisplayLearn}
                    step_completed="MODULE11_SET_stepCompleted"
                    module={module11}
                />
            );
        } else {
            // Days 4+: Load user-generated content
            setAnimalVoice(getVideo('appfiles/feelings/feelings_learn_intro.mp3', login));
            setCrabState(learnIntro);
            setShowCrab(true);

            try {
                const userSongUrl = await GetUserVideo(`userfiles/${module11.StudentID}/feelings_play_day_music.m4a`);
                const response = await fetch(userSongUrl);
                const influencerVideo = await GetUserVideo(
                    `influencers/${influencers[module11.currentday - 4]}/greet_affirmation.mp4`
                );

                const hasUserSong = response.status === 200;

                setLearnComponent(
                    <Learn
                        songVideo={video}
                        destiny="/feelings"
                        song={hasUserSong ? userSongUrl : song}
                        back="/feelings"
                        background={images.background}
                        setDisplayLearn={setDisplayLearn}
                        step_completed="MODULE11_SET_stepCompleted"
                        module={module11}
                        instrumental={hasUserSong ? getVideo('appfiles/feelings/feelings_octopus.m4a', login) : undefined}
                        influencerVideo={influencerVideo}
                    />
                );
            } catch (error) {
                console.error('Error loading learn content:', error);
            }
        }

        setDisplayLearn(true);
    };

    const openPractice = () => {
        setAnimalVoice(getVideo('appfiles/feelings/feelings_practice_intro.mp3', login));
        setCrabState(practiceIntro);
        setShowCrab(true);
        setDisplayPractice(true);
    };

    const openFTB = () => {
        // Days 4+ show intro animation
        if (module11.currentday > 3) {
            setCrabState(ftbIntro);
            setAnimalVoice(getVideo('appfiles/feelings/feelings_ftb_intro.mp3', login));
            setShowCrab(true);
        }

        setDisplayFTB(true);
    };

    // Celebrate video configuration by day
    const celebrateVideosByDay = {
        1: 'f740080030fdc3f5dc43a1ee243d/480.m3u8',
        2: '100c9b7f28b31b314a231700edce/480.m3u8',
        3: 'Module11/celebrate_day3.mp4',
    };

    const openCelebrate = async () => {
        const celebrateVideo = celebrateVideosByDay[module11.currentday];

        if (celebrateVideo) {
            // Days 1-3: Simple video celebration
            setCelebrateComponent(
                <Celebrate
                    module={module11}
                    celebrateVideo={celebrateVideo}
                    background={images.background}
                    setDisplayCelebrate={setDisplayCelebrate}
                    moduleNum={MODULE_NAME}
                />
            );
        } else {
            // Days 4+: Interactive celebration with recording
            setCrabState(celebrateIntro);
            setShowCrab(true);
            setAnimalVoice(getVideo('appfiles/greet/greet_celebrate_intro.mp3', login));

            const voice1 = getVideo('appfiles/greet/greet_celebrate_record_awesome.mp3', login);
            const voice2 = getVideo('appfiles/greet/greet_celebrate_say_thanks.mp3', login);
            const voice3 = getVideo('appfiles/eye_contact/crab_awesome_job.mp3', login);
            const animalGIF = getVideo('appfiles/feelings/feelings_joyful.gif', login);
            const animalVoiceClip = getVideo('appfiles/feelings/feelings_joyful_jingle_intro.mp3', login);

            try {
                const influencerVideo = await GetUserVideo(
                    `influencers/${influencers[module11.currentday - 4]}/greet_affirmation.mp4`
                );

                setCelebrateComponent(
                    <Celebrate
                        module={module11}
                        background={images.background}
                        voice1={voice1}
                        voice2={voice2}
                        voice3={voice3}
                        animalGIF={animalGIF}
                        animalVoice={animalVoiceClip}
                        text='Record yourself in an excited voice and say, "I am awesome!"'
                        name={`feelings_celebration_surprise_${module11.currentday + 1}.mp4`}
                        influencerVideo={influencerVideo}
                        animation={awesomeJob}
                        world3={true}
                        animal={images.animal}
                        setDisplayCelebrate={setDisplayCelebrate}
                        moduleNum={MODULE_NAME}
                    />
                );
            } catch (error) {
                console.error('Error loading celebrate content:', error);
            }
        }

        setDisplayCelebrate(true);
    };
    
    const displaySinging = () => {
        const introVideoUrl = '70eafeccd6c1f7c43050ccaac8ed/480.m3u8';
        const noteVideo = getVideo('appfiles/feelings/feelings_student_notes.mp4', login);
        const singingMusic = getVideo('appfiles/feelings/feelings_demo.m4a', login);
        const instrumental = getVideo('appfiles/feelings/feelings_instrumental.m4a', login);

        setSingTimeComponent(
            <SingingTime
                introVideo={introVideoUrl}
                demoMusic={singingMusic}
                singingVideo={noteVideo}
                background={images.background}
                singingMusic={singingMusic}
                setDisplaySingingTime={setDisplaySingingTime}
                module={module11}
                moduleNum={MODULE_NAME}
                name="feelings"
                instrumental={instrumental}
                instrumentalURL="appfiles/feelings/feelings_octopus.m4a"
            />
        );
        setDisplaySingingTime(true);
    };

    // ------------------------------------------------------------------------
    // Surprise Video Handling
    // ------------------------------------------------------------------------

    const getSurpriseSlides = async () => {
        // Configuration for surprise video slides
        const slideConfigs = [
            { video: `influencers/${login.influencerid}/greet_affirmation.mp4` },
            { video: `userfiles/${module11.StudentID}/feelings_celebration_surprise_5.mp4` },
            { video: `influencers/${login.influencerid1}/greet_affirmation.mp4` },
            { video: `userfiles/${module11.StudentID}/feelings_celebration_surprise_8.mp4` },
            { video: `influencers/${login.influencerid2}/greet_video_message.mp4` },
            { video: `influencers/${login.influencerid3}/greet_affirmation.mp4` },
            { video: `userfiles/${module11.StudentID}/feelings_celebration_surprise_9.mp4` },
            { video: `influencers/${login.influencerid4}/greet_affirmation.mp4` },
        ];

        // Load all video URLs
        const loadedSlides = await Promise.all(
            slideConfigs.map(async (slide) => ({
                video: await GetUserVideo(slide.video, login),
            }))
        );

        return loadedSlides;
    };

    const playSurpriseVideo = async () => {
        const slides = await getSurpriseSlides();

        setSurpriseVideo(
            <Surprise
                background={images.background}
                music={getVideo('appfiles/feelings/feelings_instrumental.m4a', login)}
                animal={images.animal}
                back="/"
                slides={slides}
                setDisplaySurprise={setDisplaySurprise}
            />
        );

        setDisplaySurprise(true);
        setKeyIcon(true);
        setPresentIcon(false);
    };

    // ------------------------------------------------------------------------
    // Animation Handlers
    // ------------------------------------------------------------------------

    const coinAnimation = () => {
        setCoinAnimationState('coinAnimation');
        setCoinAudio(true);
    };

    const keyAnimation = () => {
        setKeyAnimationState('fade-out');
        sleep(1000).then(chestShakeAnimation);
    };

    const chestShakeAnimation = () => {
        setChestAnimationState('shakeChest');
        setPlayChestShake(true);

        sleep(1400).then(() => {
            setKeyIcon(false);
            setChestImage(images.openChest);
            setPlayChestOpen(true);
            rewardsMovingAnimation();
        });
    };

    const rewardsMovingAnimation = () => {
        sleep(1000).then(() => {
            setDisplayReward(true);
        });
    };

    const closeDay8Video = () => {
        setIntroVideo(AFTER_CELEBRATE_VIDEO_DAY9);
        setDisplayIntroVideo(true);
        setDisplayBody(false);
        setShowCrab(false);
    };

    const playButtonAudio = () => {
        sleep(700).then(() => {
            setButtonSound(true);
        });
    };

    // ------------------------------------------------------------------------
    // Drag and Drop Handler
    // ------------------------------------------------------------------------

    const handleDragStop = (rewardId, data) => {
        const position = {
            x: data.x / clientWidth,
            y: data.y / clientHeight,
        };

        const isPrize = rewardId === 1 || rewardId === 2;
        const actionType = isPrize
            ? `MODULE11_SET_PRIZE${rewardId}_POSITION`
            : `MODULE11_SET_REWARD${rewardId}_POSITION`;

        dispatch({ type: actionType, payload: position });
    };

    // ------------------------------------------------------------------------
    // iOS Fallback Effects
    // ------------------------------------------------------------------------

    // iOS audio fallback for voice display
    useEffect(() => {
        if (!displayVoice || !isIOSDevice()) return;

        const timer = setTimeout(() => {
            setAudioReady(false);
            setShowCrab(false);
            setDisplayVoice(false);
        }, 6000);

        return () => clearTimeout(timer);
    }, [displayVoice]);

    // iOS audio fallback for awesome job audio
    useEffect(() => {
        if (!playYouAreAwesome || !isIOSDevice()) return;

        const timer = setTimeout(() => {
            if (module11.currentday === SPECIAL_DAYS.DAY_8) {
                closeDay8Video();
            } else {
                navigate(NAVIGATION_PATH);
            }
        }, 5000);

        return () => clearTimeout(timer);
    }, [playYouAreAwesome]);

    // ------------------------------------------------------------------------
    // Audio Event Handlers
    // ------------------------------------------------------------------------

    const handleCoinAudioEnd = () => {
        dispatch({ type: 'MODULE11_SET_COIN', payload: module11.coins + 1 });
        setCoinAudio(false);
        setCoinAnimationState('bounceIcons');
        setCoinIcon(false);
        playButtonAudio();
    };

    const handleVoiceAudioEnd = () => {
        setAudioReady(false);
        setShowCrab(false);
        setDisplayVoice(false);
    };

    const handleAwesomeAudioEnd = () => {
        if (module11.currentday === SPECIAL_DAYS.DAY_8) {
            closeDay8Video();
        } else {
            navigate(NAVIGATION_PATH);
        }
    };

    const handlePrizeClick = () => {
        setPrize(false);
        setChestAnimationState('shakeChest');
        setPlayChestShake(true);

        sleep(1400).then(() => {
            setChestImage(images.openChest);
            setPlayChestOpen(true);
            sleep(1000).then(() => {
                setDisplayPrize(true);
            });
        });
    };

    const handleLogout = () => {
        navigate('/');
        userHasAuthenticated(false);
        localStorage.setItem('token', 'false');
    };

    // ------------------------------------------------------------------------
    // Score Display Helper
    // ------------------------------------------------------------------------

    const getScoreClassName = () => {
        if (module11.coins >= COIN_PRIZE_THRESHOLD) return 'scoretext100';
        if (module11.coins >= 10) return 'scoretext10';
        return 'scoretext1';
    };

    // ------------------------------------------------------------------------
    // Render Helpers
    // ------------------------------------------------------------------------

    const isSpecialDay = module11.currentday === SPECIAL_DAYS.DAY_4 || module11.currentday === SPECIAL_DAYS.DAY_8;
    const progressBarKeys = isSpecialDay ? progressBar5Keys : progressBar4Keys;
    const maxKeys = isSpecialDay ? 5 : 4;

    const shouldShowMainContent =
        (displayBody && !displayLearn && !displayPractice && !displaySingingTime &&
         !displayCelebrate && !displaySurprise && !displayFTB && !displayDay4Practice) ||
        showCrab;

    const shouldShowActionButtons =
        !showCrab && !module11.reviewIcon && !coinIcon && !displayPrize && !dropZone;

    // ------------------------------------------------------------------------
    // Render
    // ------------------------------------------------------------------------

    return (
        <>
            {/* Audio Elements */}
            {playYouAreAwesome && (
                <audio autoPlay onPlay={() => setAudioReady(true)} onEnded={handleAwesomeAudioEnd} onError={handleAwesomeAudioEnd}>
                    <source src={dayFinished} />
                </audio>
            )}

            {coinAudio && (
                <audio autoPlay onEnded={handleCoinAudioEnd} onError={handleCoinAudioEnd}>
                    <source src={generateImageUrl('media/Module5Videos/earn_token.mp3')} type="audio/mp3" />
                </audio>
            )}

            {checkSound && (
                <audio autoPlay onEnded={() => setCheckSound(false)} onError={() => setCheckSound(false)}>
                    <source src={generateImageUrl('media/Module1Videos/button_accept_item.mp3')} type="audio/mp3" />
                </audio>
            )}

            {rewardsFound && (
                <audio autoPlay>
                    <source src={generateImageUrl('media/Module2Videos/daily_rewards_present.mp3')} />
                </audio>
            )}

            {touchButton && (
                <audio autoPlay onEnded={() => setTouchButton(false)} onError={() => setTouchButton(false)}>
                    <source src={generateImageUrl('media/Module1Videos/touch_button.mp3')} type="audio/mp3" />
                </audio>
            )}

            {buttonSound && (
                <audio autoPlay onEnded={() => setButtonSound(false)} onError={() => setButtonSound(false)}>
                    <source src={generateImageUrl('media/Module1Videos/button_bouncing.mp3')} type="audio/mp3" />
                </audio>
            )}

            {playChestOpen && (
                <audio autoPlay>
                    <source src={generateImageUrl('media/Module1Videos/chest_open_items_out.mp3')} />
                </audio>
            )}

            {playChestShake && (
                <audio autoPlay>
                    <source src={generateImageUrl('media/Module1Videos/chest_shake.mp3')} />
                </audio>
            )}

            {displayVoice && (
                <audio autoPlay onPlay={() => setAudioReady(true)} onEnded={handleVoiceAudioEnd} onError={handleVoiceAudioEnd}>
                    <source src={animalVoice} />
                </audio>
            )}

            {/* Activity Screens */}
            {displayIntroVideo && renderIntroduction()}
            {!showCrab && displayLearn && learnComponent}
            {!showCrab && displayPractice && (
                <Practice
                    setDisplayPractice={setDisplayPractice}
                    background={images.background}
                    stepComplete="MODULE11_SET_stepCompleted"
                    module={module11}
                    practiceConversation={practiceConversation}
                    practiceMessage={practiceMessage}
                    awesomeJob={awesomeJob}
                />
            )}
            {!showCrab && displayFTB && (
                <FollowTheBeatModule6
                    setDisplayFTB={setDisplayFTB}
                    awesomeJob={awesomeJob}
                    instruction={ftbInstruction}
                    instructionGIF={ftbInstructionGIF}
                    song={ftbMusic}
                    count={ftbCount}
                    tutorial1={ftbTutorial1}
                    tutorial2={ftbTutorial2}
                    walkthrough={ftbWalkthrough}
                    stepComplete="MODULE11_SET_stepCompleted"
                    module={module11}
                    unit3={true}
                    score="ftb_feelings"
                />
            )}
            {displaySingingTime && singTimeComponent}
            {!showCrab && displayCelebrate && celebrateComponent}
            {displaySurprise && surpriseVideo}

            {/* Main World Content */}
            {shouldShowMainContent && (
                <div className="feelingsWorldBody" style={{ backgroundImage: `url(${images.background})` }}>
                    {/* Navigation Bar */}
                    <div className="navigation">
                        <img
                            className="logoutBtn"
                            src={images.logoutBtn}
                            alt="Logout Button"
                            onClick={togglePopup}
                        />
                        <img
                            className="JourneyBlueImage"
                            src={images.journeyMap}
                            alt="Navigation blue world"
                            onClick={() => navigate(NAVIGATION_PATH)}
                        />
                    </div>

                    {/* Logout Popup */}
                    {isPopupOpen && (
                        <PopUp
                            content={
                                <>
                                    <b id="title">Are you sure you want to leave?<br /></b>
                                    <button id="logoutButton1" className="logoutButton" onClick={handleLogout}>
                                        YES
                                    </button>
                                    <button id="logoutButton2" className="logoutButton" onClick={togglePopup}>
                                        NO
                                    </button>
                                </>
                            }
                            handleClose={togglePopup}
                        />
                    )}

                    {/* Prize Elements */}
                    {prize && (
                        <audio autoPlay>
                            <source src={presentSound} />
                        </audio>
                    )}

                    {prize && (
                        <img
                            className="prize"
                            src={generateImageUrl('images/Images/prize.png')}
                            alt="Prize"
                            onClick={handlePrizeClick}
                        />
                    )}

                    {prize && (
                        <button className="prize-button" onClick={() => { setPrize(false); setDisplayPrize(true); }}>
                            <h5>Click Here</h5>
                        </button>
                    )}

                    {displayPrize && renderPrizeSelection()}

                    {/* Crab Character */}
                    <img className="animateCrab11" src={images.crab} alt="crab" />

                    {showCrab && !audioReady && <img id="loading" src={loadingGif} alt="loading" />}

                    {showCrab && (
                        <img
                            className="animateCrab11"
                            style={audioReady ? {} : { display: 'none' }}
                            src={crabState}
                            onLoad={() => {if(!playYouAreAwesome){setDisplayVoice(true)}}}
                            onError={() => setShowCrab(false)}
                            alt="crab animation"
                        />
                    )}

                    {/* Chest */}
                    <img
                        className="closedChest6"
                        src={chestImage}
                        id={chestAnimationState}
                        alt="chest"
                    />

                    {/* Score Bar */}
                    <div className="scoreBar">
                        <img className="scoreKey" src={images.coinBar} alt="coin counter" />
                        <h5 className={getScoreClassName()}>{module11.coins}</h5>
                        <p className="scoretext">100</p>
                    </div>

                    {/* Progress Bar */}
                    <div className="progressBar">
                        <img
                            className="progressKey"
                            src={progressBarKeys[module11.keys]}
                            alt="progress"
                        />
                        <h3 className="progresstext">{module11.keys}/{maxKeys}</h3>
                    </div>

                    {/* Action Buttons */}
                    {shouldShowActionButtons && (
                        <div className="actionBtnDiv">
                            {module11.currentday === SPECIAL_DAYS.DAY_4 && module11.stepCompleted === STEPS.NOT_STARTED && (
                                <img
                                    className="actionBtn2"
                                    id="bounceIcons"
                                    src={images.singingTimeBtn}
                                    alt="singing time"
                                    onClick={() => { setTouchButton(true); sleep(500).then(() => { displaySinging(); setCheckStep(STEPS.SINGING_TIME); }); }}
                                />
                            )}

                            {module11.currentday !== SPECIAL_DAYS.DAY_4 && module11.stepCompleted === STEPS.NOT_STARTED && (
                                <img
                                    className="actionBtn2"
                                    id="bounceIcons"
                                    src={images.learnBtn}
                                    alt="learn"
                                    onClick={() => { setTouchButton(true); sleep(500).then(() => { openLearn(); setCheckStep(STEPS.LEARN); }); }}
                                />
                            )}

                            {module11.stepCompleted === STEPS.LEARN && (
                                <img
                                    className="actionBtn2"
                                    id="bounceIcons"
                                    src={images.practiceBtn}
                                    alt="practice"
                                    onClick={() => { touchSound.play(); sleep(500).then(() => { openPractice(); setCheckStep(STEPS.PRACTICE); }); }}
                                />
                            )}

                            {module11.stepCompleted === STEPS.PRACTICE && (
                                <img
                                    className="actionBtn2"
                                    id="bounceIcons"
                                    src={images.ftbBtn}
                                    alt="follow the beat"
                                    onClick={() => { setTouchButton(true); sleep(500).then(() => { openFTB(); setCheckStep(STEPS.FOLLOW_THE_BEAT); }); }}
                                />
                            )}

                            {module11.stepCompleted === STEPS.FOLLOW_THE_BEAT && (
                                <img
                                    className="actionBtn2"
                                    id="bounceIcons"
                                    src={images.celebrateBtn}
                                    alt="celebrate"
                                    onClick={() => { setTouchButton(true); sleep(500).then(() => { openCelebrate(); setCheckStep(STEPS.CELEBRATE); }); }}
                                />
                            )}

                            {module11.stepCompleted === STEPS.SINGING_TIME && (
                                <img
                                    className="actionBtn2"
                                    id="bounceIcons"
                                    src={images.learnBtn}
                                    alt="learn"
                                    onClick={() => { setTouchButton(true); sleep(500).then(() => { openLearn(); setCheckStep(STEPS.LEARN); }); }}
                                />
                            )}
                        </div>
                    )}

                    {/* Coin Icon */}
                    {!displayVoice && coinIcon && !displayPrize && (
                        <div className="actionBtnDiv">
                            {!module11.reviewIcon && (
                                <img
                                    id={coinAnimationState}
                                    src={images.coinPick}
                                    alt="coin"
                                    style={{
                                        position: 'absolute',
                                        top: '50vh',
                                        left: '50vw',
                                        width: '100px',
                                        height: '100px',
                                        marginLeft: '-50px',
                                        marginTop: '-50px',
                                        zIndex: 10,
                                    }}
                                    onClick={coinAnimation}
                                />
                            )}
                        </div>
                    )}

                    {/* Review and Reward Buttons */}
                    {!displayPrize && !showCrab && !displayReward && (
                        <div className="actionBtnDiv">
                            {module11.reviewIcon && (
                                <img
                                    src={images.reviewBtn}
                                    className={showReward ? 'actionBtn3' : 'actionBtn2'}
                                    alt="review"
                                    onClick={() => { setTouchButton(true); sleep(500).then(() => setReviewIconVisible(!reviewIconVisible)); }}
                                />
                            )}

                            {showReward && (
                                <img
                                    src={images.rewardBtn}
                                    className="actionBtn4"
                                    alt="reward"
                                    onClick={() => { setTouchButton(true); sleep(500).then(keyAnimation); }}
                                />
                            )}

                            {module11.reviewIcon && reviewIconVisible && renderReviewButtons()}
                        </div>
                    )}

                    {/* Key Icon */}
                    {keyIcon && !coinIcon && !displayPrize && (
                        <img
                            src={images.keyReward}
                            id={keyAnimationState}
                            style={{ width: '150px', height: '50px', marginLeft: '-75px', zIndex: 10 }}
                            className="rewardsIcon"
                            alt="key"
                            onClick={keyAnimation}
                        />
                    )}

                    {displayReward && renderRewardSelection()}

                    {/* Present Icon */}
                    {!displayPrize && !coinIcon && presentIcon && (
                        <>
                            <img
                                className="present"
                                style={{ width: '84px', height: '100px', marginLeft: '-42px', zIndex: 10 }}
                                id="bounceIcons"
                                src={images.present}
                                alt="gift"
                                onClick={playSurpriseVideo}
                            />
                            <audio autoPlay>
                                <source src={presentSound} />
                            </audio>
                        </>
                    )}

                    {/* Draggable Rewards Area */}
                    <div className="rewardArea">
                        {/* Day 1 Rewards: Surfboards */}
                        {module11.rewards.SurfBoardRed.unlocked && (
                            <Draggable
                                disabled={!displayGreenButton}
                                bounds="parent"
                                position={{
                                    x: module11.rewards.SurfBoardRed.x * clientWidth,
                                    y: module11.rewards.SurfBoardRed.y * clientHeight,
                                }}
                                onStop={(e, data) => handleDragStop(11, data)}
                            >
                                <div
                                    className="moveSurfBoard1"
                                    style={{ backgroundImage: `url(${images.rewards.SurfBoardRed})` }}
                                />
                            </Draggable>
                        )}

                        {module11.rewards.SurfBoardBlue.unlocked && (
                            <Draggable
                                disabled={!displayGreenButton}
                                bounds="parent"
                                position={{
                                    x: module11.rewards.SurfBoardBlue.x * clientWidth,
                                    y: module11.rewards.SurfBoardBlue.y * clientHeight,
                                }}
                                onStop={(e, data) => handleDragStop(12, data)}
                            >
                                <div
                                    className="moveSurfBoard2"
                                    style={{ backgroundImage: `url(${images.rewards.SurfBoardBlue})` }}
                                />
                            </Draggable>
                        )}

                        {/* Day 2 Rewards: Coral */}
                        {module11.rewards.CoralBlue.unlocked && (
                            <Draggable
                                disabled={!displayGreenButton}
                                bounds="parent"
                                position={{
                                    x: module11.rewards.CoralBlue.x * clientWidth,
                                    y: module11.rewards.CoralBlue.y * clientHeight,
                                }}
                                onStop={(e, data) => handleDragStop(21, data)}
                            >
                                <div
                                    className="moveReward1"
                                    style={{ backgroundImage: `url(${images.rewards.CoralBlue})` }}
                                />
                            </Draggable>
                        )}

                        {module11.rewards.CoralRed.unlocked && (
                            <Draggable
                                disabled={!displayGreenButton}
                                bounds="parent"
                                position={{
                                    x: module11.rewards.CoralRed.x * clientWidth,
                                    y: module11.rewards.CoralRed.y * clientHeight,
                                }}
                                onStop={(e, data) => handleDragStop(22, data)}
                            >
                                <div
                                    className="moveReward2"
                                    style={{ backgroundImage: `url(${images.rewards.CoralRed})` }}
                                />
                            </Draggable>
                        )}

                        {/* Day 3 Rewards: Floatation */}
                        {module11.rewards.FloatationInnerTube.unlocked && (
                            <Draggable
                                disabled={!displayGreenButton}
                                bounds="parent"
                                position={{
                                    x: module11.rewards.FloatationInnerTube.x * clientWidth,
                                    y: module11.rewards.FloatationInnerTube.y * clientHeight,
                                }}
                                onStop={(e, data) => handleDragStop(31, data)}
                            >
                                <div
                                    className="moveReward1"
                                    style={{ backgroundImage: `url(${images.rewards.FloatationInnerTube})` }}
                                />
                            </Draggable>
                        )}

                        {module11.rewards.FloatationBall.unlocked && (
                            <Draggable
                                disabled={!displayGreenButton}
                                bounds="parent"
                                position={{
                                    x: module11.rewards.FloatationBall.x * clientWidth,
                                    y: module11.rewards.FloatationBall.y * clientHeight,
                                }}
                                onStop={(e, data) => handleDragStop(32, data)}
                            >
                                <div
                                    className="moveReward2"
                                    style={{ backgroundImage: `url(${images.rewards.FloatationBall})` }}
                                />
                            </Draggable>
                        )}

                        {/* Day 4 Rewards: Seashells */}
                        {module11.rewards.SeashellsRed.unlocked && (
                            <Draggable
                                disabled={!displayGreenButton}
                                bounds="parent"
                                position={{
                                    x: module11.rewards.SeashellsRed.x * clientWidth,
                                    y: module11.rewards.SeashellsRed.y * clientHeight,
                                }}
                                onStop={(e, data) => handleDragStop(41, data)}
                            >
                                <div
                                    className="moveReward1"
                                    style={{ backgroundImage: `url(${images.rewards.SeashellsRed})` }}
                                />
                            </Draggable>
                        )}

                        {module11.rewards.SeashellsGreen.unlocked && (
                            <Draggable
                                disabled={!displayGreenButton}
                                bounds="parent"
                                position={{
                                    x: module11.rewards.SeashellsGreen.x * clientWidth,
                                    y: module11.rewards.SeashellsGreen.y * clientHeight,
                                }}
                                onStop={(e, data) => handleDragStop(42, data)}
                            >
                                <div
                                    className="moveReward2"
                                    style={{ backgroundImage: `url(${images.rewards.SeashellsGreen})` }}
                                />
                            </Draggable>
                        )}

                        {/* Day 5 Rewards: Mermaids */}
                        {module11.rewards.MermaidRed.unlocked && (
                            <Draggable
                                disabled={!displayGreenButton}
                                bounds="parent"
                                position={{
                                    x: module11.rewards.MermaidRed.x * clientWidth,
                                    y: module11.rewards.MermaidRed.y * clientHeight,
                                }}
                                onStop={(e, data) => handleDragStop(51, data)}
                            >
                                <div
                                    className="moveReward1"
                                    style={{ backgroundImage: `url(${images.rewards.MermaidRed})` }}
                                />
                            </Draggable>
                        )}

                        {module11.rewards.MermaidBlue.unlocked && (
                            <Draggable
                                disabled={!displayGreenButton}
                                bounds="parent"
                                position={{
                                    x: module11.rewards.MermaidBlue.x * clientWidth,
                                    y: module11.rewards.MermaidBlue.y * clientHeight,
                                }}
                                onStop={(e, data) => handleDragStop(52, data)}
                            >
                                <div
                                    className="moveReward2"
                                    style={{ backgroundImage: `url(${images.rewards.MermaidBlue})` }}
                                />
                            </Draggable>
                        )}

                        {/* Day 6 Rewards: Clams */}
                        {module11.rewards.ClamBlue.unlocked && (
                            <Draggable
                                disabled={!displayGreenButton}
                                bounds="parent"
                                position={{
                                    x: module11.rewards.ClamBlue.x * clientWidth,
                                    y: module11.rewards.ClamBlue.y * clientHeight,
                                }}
                                onStop={(e, data) => handleDragStop(61, data)}
                            >
                                <div
                                    className="moveClam1"
                                    style={{ backgroundImage: `url(${images.rewards.ClamBlue})` }}
                                />
                            </Draggable>
                        )}

                        {module11.rewards.ClamPink.unlocked && (
                            <Draggable
                                disabled={!displayGreenButton}
                                bounds="parent"
                                position={{
                                    x: module11.rewards.ClamPink.x * clientWidth,
                                    y: module11.rewards.ClamPink.y * clientHeight,
                                }}
                                onStop={(e, data) => handleDragStop(62, data)}
                            >
                                <div
                                    className="moveClam2"
                                    style={{ backgroundImage: `url(${images.rewards.ClamPink})` }}
                                />
                            </Draggable>
                        )}

                        {/* Day 7 Rewards: Seaweed */}
                        {module11.rewards.SeaweedBrown.unlocked && (
                            <Draggable
                                disabled={!displayGreenButton}
                                bounds="parent"
                                position={{
                                    x: module11.rewards.SeaweedBrown.x * clientWidth,
                                    y: module11.rewards.SeaweedBrown.y * clientHeight,
                                }}
                                onStop={(e, data) => handleDragStop(71, data)}
                            >
                                <div
                                    className="moveReward1"
                                    style={{ backgroundImage: `url(${images.rewards.SeaweedBrown})` }}
                                />
                            </Draggable>
                        )}

                        {module11.rewards.SeaweedGreen.unlocked && (
                            <Draggable
                                disabled={!displayGreenButton}
                                bounds="parent"
                                position={{
                                    x: module11.rewards.SeaweedGreen.x * clientWidth,
                                    y: module11.rewards.SeaweedGreen.y * clientHeight,
                                }}
                                onStop={(e, data) => handleDragStop(72, data)}
                            >
                                <div
                                    className="moveReward2"
                                    style={{ backgroundImage: `url(${images.rewards.SeaweedGreen})` }}
                                />
                            </Draggable>
                        )}

                        {/* Day 8 Rewards: Crab Accessories */}
                        {module11.rewards.CrabwearGlasses.unlocked && (
                            <Draggable
                                disabled={!displayGreenButton}
                                bounds="parent"
                                position={{
                                    x: module11.rewards.CrabwearGlasses.x * clientWidth,
                                    y: module11.rewards.CrabwearGlasses.y * clientHeight,
                                }}
                                onStop={(e, data) => handleDragStop(81, data)}
                            >
                                <div
                                    className="moveCharaterReward1"
                                    style={{ backgroundImage: `url(${images.rewards.CrabwearGlasses})` }}
                                />
                            </Draggable>
                        )}

                        {module11.rewards.CrabWearMustash.unlocked && (
                            <Draggable
                                disabled={!displayGreenButton}
                                bounds="parent"
                                position={{
                                    x: module11.rewards.CrabWearMustash.x * clientWidth,
                                    y: module11.rewards.CrabWearMustash.y * clientHeight,
                                }}
                                onStop={(e, data) => handleDragStop(82, data)}
                            >
                                <div
                                    className="moveCharaterReward2"
                                    style={{ backgroundImage: `url(${images.rewards.CrabWearMustash})` }}
                                />
                            </Draggable>
                        )}

                        {/* Prize Rewards: Castle and Treasure */}
                        {module11.rewards.SandCastle.unlocked && (
                            <Draggable
                                disabled={!displayGreenButton}
                                bounds="parent"
                                position={{
                                    x: module11.rewards.SandCastle.x * clientWidth,
                                    y: module11.rewards.SandCastle.y * clientHeight,
                                }}
                                onStop={(e, data) => handleDragStop(1, data)}
                            >
                                <div
                                    className="movePrize11"
                                    style={{ backgroundImage: `url(${images.rewards.SandCastle})` }}
                                />
                            </Draggable>
                        )}

                        {module11.rewards.Treasure.unlocked && (
                            <Draggable
                                disabled={!displayGreenButton}
                                bounds="parent"
                                position={{
                                    x: module11.rewards.Treasure.x * clientWidth,
                                    y: module11.rewards.Treasure.y * clientHeight,
                                }}
                                onStop={(e, data) => handleDragStop(2, data)}
                            >
                                <div
                                    className="movePrize112"
                                    style={{ backgroundImage: `url(${images.rewards.Treasure})` }}
                                />
                            </Draggable>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default Module11World;